import moment from 'moment';
import vuex from '@/store';
import API, { Types } from '@/modules/API';
import Utils from '@/modules/Utils';
import MQTT from '@/modules/MQTT';
import { runAdaptivePool } from '@/modules/ConcurrencyPool';
import { invalidateCabinetSnapshot } from '@/pages/cabinetManagement/services/CabinetManagementApi';
import { i18n } from '@/main';

export type RetrofitDirection = 'mark' | 'return';

export const PENDING_VERIFICATION_SYS_ACTIVE = -4;
export const RELOAD_COMMAND = 'AT+reload';
export const COMMAND_STREAM = 'command';
export const SYS_ACTIVE_STREAM = 'sys___active';
export const REBOOT_COMMAND = 'reboot';
export const ACTIONS_STREAM = 'sys___actions';

export const RETROFIT_STATE = 'retrofit';
export const DELIVERED_STATE = 'delivered';
export const PARTIAL_RETROFIT_STATE = 'partial_retrofit';

export const RETROFIT_RECORD_PROP = 'meta.retrofit_record';
export const SCHEDULE_PROP = 'device.schedule';
export const SCHEDULE_CURVE_PROP = 'meta.schedule_curve';
export const SERVICES_CONFIG_PROP = 'services.config';
export const DEVICES_SCHEDULE_PROP = 'meta.devices_schedule';

export const CLEARED_SCHEDULE = { schedule: [] };
export const CLEARED_CABINET_SCHEDULE = {};
export const CLEARED_SCHEDULE_CURVE = {};

// meta.retrofit_record's schema lists all four fields as `required` and sets
// `additionalProperties: false`, so every write must send the complete object and
// nothing else. Marking a device happens before it ever returns, so `return_date`
// has no real value yet - it's sent as the schema's own default (0, "not set")
// rather than omitted, which the server rejects.
export const RETROFIT_RECORD_DEFAULTS = {
  moved_to_retrofit_date: 0,
  return_date: 0,
  connectivity_state_on_entry: -999,
  disconnect_reason: 'none'
};

// The enum from the schema. sys___active/disconnect_reason come off the device row
// and aren't guaranteed to hold one of these, so anything else falls back to 'none'.
const DISCONNECT_REASONS = [
  'none',
  'communication_fault',
  'electrical_fault',
  'daytime_light_fault',
  'active',
  'in_maintenance',
  'in_installation'
];

// `updater` is `oneOf` an email or the literal 'none' - an empty/missing username
// would fail both.
function getUpdater() {
  const state: any = vuex.state;
  return state.User.username || 'none';
}

// Every operation-state and retrofit-record value goes through here, so a missing or
// wrongly-typed field can't reach the server and fail the whole device on a 400.
function normalizeRetrofitRecord(record) {
  const source = record && typeof record === 'object' ? record : {};

  // Deliberately strict rather than coercing: Number(null) and Number('') are both 0,
  // which would turn "never captured" into a real-looking value (and 0 isn't even
  // valid for connectivity_state_on_entry).
  const asInteger = (value, fallback, min?: number) => {
    if (typeof value !== 'number' || !Number.isInteger(value)) return fallback;
    return min !== undefined && value < min ? fallback : value;
  };

  return {
    // both dates are `format: unix-s` with `minimum: 0`
    moved_to_retrofit_date: asInteger(source.moved_to_retrofit_date, RETROFIT_RECORD_DEFAULTS.moved_to_retrofit_date, 0),
    return_date: asInteger(source.return_date, RETROFIT_RECORD_DEFAULTS.return_date, 0),
    connectivity_state_on_entry: asInteger(
      source.connectivity_state_on_entry,
      RETROFIT_RECORD_DEFAULTS.connectivity_state_on_entry
    ),
    disconnect_reason: DISCONNECT_REASONS.includes(source.disconnect_reason)
      ? source.disconnect_reason
      : RETROFIT_RECORD_DEFAULTS.disconnect_reason
  };
}

export interface DeviceBatchResult {
  device_id: string;
  device_name: string;
  status: 'success' | 'failed';
  failed_step?: string;
  reason?: string;
}

// Managing retrofit is granted by exactly two things: the dedicated Cerbos role (TB-733)
// for Cerbos users, and the legacy CA (company admin) role. No other legacy role qualifies -
// a manager or CM does not. Every retrofit entry point goes through this single helper.
export const RETROFIT_ROLE = 'retrofit_user';
export const CERBOS_ROLE = 'XX';
export const COMPANY_ADMIN_ROLE = 'CA';

export function canManageRetrofit() {
  const state: any = vuex.state;
  if (state.User.role === COMPANY_ADMIN_ROLE) return true;
  // roles holds the Cerbos roles in scope for the selected project, so it's re-evaluated
  // per project - the same user can be a retrofit user on one project and not on another.
  const roles = state.User.roles;
  return state.User.role === CERBOS_ROLE && Array.isArray(roles) && roles.includes(RETROFIT_ROLE);
}

export function isCabinetDevice(device) {
  return !!device && Utils.hasCabinetClass(device['meta.category'] && device['meta.category'].category);
}

// Not every device type holds both schedule props: cabinets have no
// meta.schedule_curve, virtual cabinets have no device.schedule, and virtual
// controllers have neither, so those props must not be patched for them.
function isVirtualCabinet(device) {
  if (!device) return false;
  return Utils.hasVirtualCabinetClass(device['meta.category']) || device.class_name === 'virtual_cabinet';
}

function isVirtualController(device) {
  return !!device && Utils.hasVirtualControllerClass(device.class_name);
}

function hasScheduleProp(device) {
  return !isVirtualCabinet(device) && !isVirtualController(device);
}

function hasScheduleCurveProp(device) {
  return !isCabinetDevice(device) && !isVirtualController(device);
}

export function isRetrofitDevice(device) {
  return Utils.getOperationState(device) === RETROFIT_STATE;
}

function getMqttVersion(device) {
  const state: any = vuex.state;
  const version_by_class = state.Global.mqtt_version_by_class;
  return version_by_class && version_by_class.has(device.class_name)
    ? version_by_class.get(device.class_name).mqtt_version
    : 3;
}

function publishToDevice(device, stream_name, stream_value, message_type) {
  // MQTT.instance.init() is idempotent (no-ops once a client already exists) but
  // must be called somewhere - unlike the per-device info tabs, nothing on the
  // retrofit path guarantees a connection exists before this publish runs.
  MQTT.instance.init();

  const version_num = getMqttVersion(device);
  MQTT.instance.publishToSingleDevice({
    version_num,
    device_id: device.id,
    stream_id: version_num === 3 ? `${device.id}.${stream_name}` : stream_name,
    message_type,
    format: 'minimal',
    // mqtt.js only accepts a string/Buffer payload - a raw number is dropped
    stream_value: String(stream_value),
    options: {
      retain: false,
      qos: 1
    }
  });
}

// A state the dashboard reports on the device's behalf, not an instruction.
function publishDeviceEvent(device, stream_name, stream_value) {
  publishToDevice(device, stream_name, stream_value, 'evt');
}

function publishDeviceCommand(device, stream_name, stream_value) {
  publishToDevice(device, stream_name, stream_value, 'cmd');
}

function nowInSeconds() {
  return Math.floor(moment().utcOffset(0).valueOf() / 1000);
}

/**
 * Devices are often already physically under Retrofit by the time someone marks them in
 * the dashboard, so the panel lets the user name the day it actually happened.
 *
 * The picker offers a day, not an instant, so the chosen day is combined with the current
 * time of day rather than midnight - a backdated entry reads as "07/09/2026 14:32", not
 * "07/09/2026 00:00". Picking today therefore resolves to now, which is why an untouched
 * picker needs no special case.
 */
export function resolveRetrofitEntryDate(entry_date?: string): number {
  if (!entry_date) return nowInSeconds();

  const picked = moment(entry_date, 'YYYY-MM-DD', true);
  if (!picked.isValid()) return nowInSeconds();

  const now = moment();
  const stamped = picked.set({
    hour: now.hour(),
    minute: now.minute(),
    second: now.second(),
    millisecond: 0
  });

  return Math.floor(stamped.valueOf() / 1000);
}

function editDeviceProp(device_id, name, value) {
  return API.patch(Types.DEVICES, 'properties', value, {
    itemID: device_id,
    query: { name }
  });
}

async function fetchDeviceProp(device_id, name) {
  const response = await API.get(Types.DEVICES, `${device_id}/properties`, { name });
  const result = response && response.results && response.results[0];
  if (!result) return null;
  // some properties come back as a JSON string instead of an object
  if (typeof result.value !== 'string') return result.value;
  try {
    return JSON.parse(result.value);
  } catch (e) {
    return result.value;
  }
}

function getClearedSchedule(device) {
  return isCabinetDevice(device) ? CLEARED_CABINET_SCHEDULE : CLEARED_SCHEDULE;
}

function isScheduleCleared(device, value) {
  if (!value || typeof value !== 'object') return true;
  return isCabinetDevice(device)
    ? !Object.keys(value).length
    : !Array.isArray(value.schedule) || !value.schedule.length;
}

function isScheduleCurveCleared(device, value) {
  if (!value || typeof value !== 'object') return true;
  return !Object.keys(value).length;
}

// Clears a schedule property and confirms the reset actually persisted.
// The store copy is authoritative for skipping the write - both properties are
// streamed into the store - so an already-empty value costs no PATCH. A stale
// store copy (or a silently ignored PATCH) is still caught by the read-back,
// which then forces the write once instead of failing the device.
async function clearScheduleProp(device, prop, cleared_value, is_cleared) {
  if (!is_cleared(device, device[prop])) await editDeviceProp(device.id, prop, cleared_value);

  let current = await fetchDeviceProp(device.id, prop);
  if (!is_cleared(device, current)) {
    await editDeviceProp(device.id, prop, cleared_value);
    current = await fetchDeviceProp(device.id, prop);
    // A translatable, fixed message - the raw property name means nothing to
    // the user and must not leak into the failure reason shown in the UI.
    if (!is_cleared(device, current)) throw new Error('The device schedule could not be cleared');
  }

  // mirror the cleared value onto the store copy so schedule-dependent UI
  // (map icons, schedule tabs) updates before the next devices fetch
  vuex.commit('Reports/editDeviceField', {
    device_id: device.id,
    name: prop,
    value: { ...cleared_value }
  });
}

// cabinet.operation_state / pole.operation_state both require exactly
// state + updater + date, with `additionalProperties: false`.
function buildOperationStateValue(device, operation_state, date_in_seconds?: number) {
  const state_key = isCabinetDevice(device) ? 'cabinet_opstate' : 'state';
  return {
    [state_key]: operation_state,
    updater: getUpdater(),
    // The date the state took effect, which the user can backdate for Retrofit. Readers
    // must not treat this as a write time - see setOperationState's store mirror, which
    // is what tells them this value is the current one.
    date: date_in_seconds || nowInSeconds()
  };
}

export async function setCabinetOperationState(cabinet, operation_state, date_in_seconds?: number) {
  const result = await setOperationState(cabinet, operation_state, date_in_seconds);

  // A cabinet can also reach full Retrofit derived from its fixtures (all of
  // them marked), without the controller itself being part of the batch - the
  // services then have to be switched here, since the controller never ran
  // through getMarkAsRetrofitSteps' services_config step.
  //
  // These run even when the cabinet was already in the state (setOperationState
  // then wrote nothing): it may have been moved there before these properties
  // were part of the flow, or a previous run may have failed halfway, so they are
  // re-checked every time. Every one of them is idempotent.
  if (operation_state === RETROFIT_STATE) {
    await applyRetrofitCabinetServices(cabinet);
    await deactivateCabinetDevicesSchedule(cabinet);
  } else if (operation_state === PARTIAL_RETROFIT_STATE) {
    // Partial Retrofit is the one state that touches nothing else: its fixtures are
    // only partly out, so the cabinet keeps driving its devices schedule and keeps
    // its alert services - only the Power/Schedule validation service is turned on.
    await applyPartialRetrofitCabinetServices(cabinet);
  }

  return result;
}

export async function setOperationState(device, operation_state, date_in_seconds?: number) {
  const prop = Utils.getOperationStatePropName(device);

  // `date` records when the device entered the state, so re-writing a state it is
  // already in would move that date to now (or to whatever the entry-date picker
  // says) and lose the original one. A device whose row doesn't carry the property
  // reads as '' here and is written, so an unknown state is never assumed - and the
  // row is only trusted as a cheap pre-check, the persisted value decides.
  if (Utils.getOperationState(device) === operation_state) {
    const persisted = await fetchDeviceProp(device.id, prop);
    const persisted_state = persisted && typeof persisted === 'object'
      ? persisted.cabinet_opstate || persisted.state || persisted.operation_state || ''
      : '';
    if (persisted_state === operation_state) return;
  }

  const value = buildOperationStateValue(device, operation_state, date_in_seconds);

  await editDeviceProp(device.id, prop, value);

  // mirror onto the store copy so map icons and status labels update before
  // the next devices fetch
  vuex.commit('Reports/editDeviceField', {
    device_id: device.id,
    name: prop,
    value: { ...value }
  });

  // Cabinet Management serves its own cached list for 10 minutes and doesn't
  // read the Reports store, so it must refetch to pick up the new state.
  // This includes fixture (pole) changes, not just the cabinet controller
  // itself - the cached list's per-cabinet retrofit_count/percentage is an
  // aggregate over that cabinet's fixtures, fetched via fetchAllFixtureCounts.
  invalidateCabinetSnapshot();
}

// The single choke point for every meta.retrofit_record write, so the schema-complete
// shape is guaranteed here rather than depended on from each builder.
async function setRetrofitRecord(device, record) {
  const full_record = normalizeRetrofitRecord(record);

  await editDeviceProp(device.id, RETROFIT_RECORD_PROP, full_record);

  // mirror onto the store copy so the next step in this same batch (or a
  // subsequent retrofit action in this session) reads the merged record
  // instead of a stale one and unintentionally drops earlier fields
  vuex.commit('Reports/editDeviceField', {
    device_id: device.id,
    name: RETROFIT_RECORD_PROP,
    value: { ...full_record }
  });
}

// The devices list doesn't include meta.retrofit_record, so the in-memory copy
// only exists when this same session wrote it. Reading the persisted value keeps
// earlier fields (moved_to_retrofit_date, connectivity_state_on_entry, ...) when
// merging in a new one, instead of replacing the whole record.
async function getExistingRetrofitRecord(device) {
  let persisted = null;

  try {
    persisted = await fetchDeviceProp(device.id, RETROFIT_RECORD_PROP);
  } catch (e) {
    persisted = null;
  }

  if (persisted && typeof persisted === 'object') return persisted;

  return device[RETROFIT_RECORD_PROP] && typeof device[RETROFIT_RECORD_PROP] === 'object'
    ? device[RETROFIT_RECORD_PROP]
    : {};
}

async function buildRetrofitEntryRecord(device, date_in_seconds?: number) {
  // A device that never reported connectivity has no state to capture, which the
  // schema expresses as -999 rather than a missing field.
  const record: any = {
    ...(await getExistingRetrofitRecord(device)),
    moved_to_retrofit_date: date_in_seconds || nowInSeconds(),
    connectivity_state_on_entry: Number.isInteger(device.sys___active)
      ? device.sys___active
      : RETROFIT_RECORD_DEFAULTS.connectivity_state_on_entry
  };

  if (device.sys___active === 0 && device.disconnect_reason) {
    record.disconnect_reason = device.disconnect_reason;
  }

  return record;
}

async function buildRetrofitReturnRecord(device) {
  return {
    ...(await getExistingRetrofitRecord(device)),
    return_date: nowInSeconds()
  };
}

// The 3 cabinet services (Alerts, Cabinet IQ, Power/Schedule) are all driven by
// kill switches inside services.config. A cabinet under Retrofit shouldn't raise
// alerts, so entering Retrofit turns both alert services off - while the
// Power/Schedule validation service is turned ON, since it's what keeps
// validating the cabinet while it's being retrofitted. Only sections that
// already exist are touched - the Advanced tab shows a service only when its
// config section is present, so an absent section means the service isn't
// provisioned for this cabinet at all.
function buildRetrofitServicesConfig(config) {
  const next = { ...(config && typeof config === 'object' ? config : {}) };
  let changed = false;

  if (next.alerts && typeof next.alerts === 'object') {
    const alerts = { ...next.alerts };
    if (alerts.kill_switch_analytics_alerts !== true) {
      alerts.kill_switch_analytics_alerts = true;
      changed = true;
    }
    if (alerts.kill_switch_cabinet_alerts !== true) {
      alerts.kill_switch_cabinet_alerts = true;
      changed = true;
    }
    next.alerts = alerts;
  }

  if (next.schedule_validation && typeof next.schedule_validation === 'object') {
    if (next.schedule_validation.kill_switch !== false) {
      // `last_change` stamps the moment the service was killed (see
      // CabinetDetails' updateServiceStatus, which only writes it when the kill
      // switch goes on), so switching the service back on leaves it as is.
      next.schedule_validation = {
        ...next.schedule_validation,
        kill_switch: false
      };
      changed = true;
    }
  }

  return changed ? next : null;
}

// A cabinet under Retrofit must stop driving its fixtures' on/off schedule, so
// its meta.devices_schedule is deactivated (active: false) while every other
// field of the schedule is preserved, ready to be switched back on when the
// cabinet returns. Nothing is written when there's no schedule, or when it's
// already inactive.
async function deactivateCabinetDevicesSchedule(device) {
  let schedule = await fetchDeviceProp(device.id, DEVICES_SCHEDULE_PROP);

  if (!schedule || typeof schedule !== 'object') {
    schedule = device[DEVICES_SCHEDULE_PROP] && typeof device[DEVICES_SCHEDULE_PROP] === 'object'
      ? device[DEVICES_SCHEDULE_PROP]
      : null;
  }

  if (!schedule || schedule.active !== true) return;

  const next_schedule = { ...schedule, active: false };

  await editDeviceProp(device.id, DEVICES_SCHEDULE_PROP, next_schedule);

  // meta.devices_schedule drives the cabinet header's schedule chip and both
  // the cabinet's and its fixtures' map icons (getMapIcon's scheduleIsActive),
  // so the store copy has to be updated for those to follow immediately.
  vuex.commit('Reports/editDeviceField', {
    device_id: device.id,
    name: DEVICES_SCHEDULE_PROP,
    value: next_schedule
  });
}

// Partial Retrofit only switches the Power/Schedule validation service on. The two
// alert services are deliberately left exactly as the user set them - a partially
// retrofitted cabinet still has live fixtures worth raising alerts for.
function buildPartialRetrofitServicesConfig(config) {
  const next = { ...(config && typeof config === 'object' ? config : {}) };

  if (!next.schedule_validation || typeof next.schedule_validation !== 'object') return null;
  if (next.schedule_validation.kill_switch === false) return null;

  // `last_change` stamps the moment the service was killed, so switching it back
  // on leaves it as is (see buildRetrofitServicesConfig).
  next.schedule_validation = {
    ...next.schedule_validation,
    kill_switch: false
  };

  return next;
}

// `build_next` returns the config to persist, or null when there is nothing to
// change - which keeps every caller idempotent.
async function updateCabinetServicesConfig(device, build_next) {
  // Read the persisted config rather than the store copy: services.config isn't
  // part of the devices stream, so the row usually doesn't carry it at all.
  // A failure here must surface as a failed step - silently skipping would
  // report the cabinet as fully marked while its services stayed on.
  let config = await fetchDeviceProp(device.id, SERVICES_CONFIG_PROP);

  if (!config || typeof config !== 'object') {
    config = device[SERVICES_CONFIG_PROP] && typeof device[SERVICES_CONFIG_PROP] === 'object'
      ? device[SERVICES_CONFIG_PROP]
      : null;
  }

  const next_config = build_next(config);
  // Nothing to write when the switches already hold the target state (or when
  // this cabinet has none of the service sections provisioned).
  if (!next_config) return;

  await editDeviceProp(device.id, SERVICES_CONFIG_PROP, next_config);

  vuex.commit('Reports/editDeviceField', {
    device_id: device.id,
    name: SERVICES_CONFIG_PROP,
    value: next_config
  });
}

function applyRetrofitCabinetServices(device) {
  return updateCabinetServicesConfig(device, buildRetrofitServicesConfig);
}

function applyPartialRetrofitCabinetServices(device) {
  return updateCabinetServicesConfig(device, buildPartialRetrofitServicesConfig);
}

function getMarkAsRetrofitSteps(device, date_in_seconds?: number) {
  const already_in_retrofit = Utils.getOperationState(device) === RETROFIT_STATE;

  const steps: Array<{ name: string; run: () => Promise<any> }> = [
    {
      name: 'operation_state',
      run: () => setOperationState(device, RETROFIT_STATE, date_in_seconds)
    }
  ];

  // A device already in Retrofit keeps the record stamped when it first entered -
  // re-running the sequence to fix its remaining properties must not overwrite
  // moved_to_retrofit_date / connectivity_state_on_entry with today's values.
  if (!already_in_retrofit) {
    steps.push({
      name: 'retrofit_record',
      run: async () => setRetrofitRecord(device, await buildRetrofitEntryRecord(device, date_in_seconds))
    });
  }

  if (isCabinetDevice(device)) {
    steps.push({
      name: 'services_config',
      run: () => applyRetrofitCabinetServices(device)
    });
    steps.push({
      name: 'devices_schedule',
      run: () => deactivateCabinetDevicesSchedule(device)
    });
  }

  if (hasScheduleProp(device)) {
    steps.push({
      name: 'schedule',
      run: () => clearScheduleProp(device, SCHEDULE_PROP, getClearedSchedule(device), isScheduleCleared)
    });
  }

  if (hasScheduleCurveProp(device)) {
    steps.push({
      name: 'schedule_curve',
      run: () => clearScheduleProp(device, SCHEDULE_CURVE_PROP, CLEARED_SCHEDULE_CURVE, isScheduleCurveCleared)
    });
  }

  return steps;
}

function getReturnFromRetrofitSteps(device) {
  const steps = [
    {
      name: 'operation_state',
      run: () => setOperationState(device, DELIVERED_STATE)
    },
    {
      name: 'retrofit_record',
      run: async () => setRetrofitRecord(device, await buildRetrofitReturnRecord(device))
    }
  ];

  // A device that already reports itself as connected needs neither the
  // pending-verification sentinel nor a reload.
  if (device.sys___active === 1) return steps;

  return [
    ...steps,
    {
      name: 'pending_verification_event',
      run: async () => publishDeviceEvent(device, SYS_ACTIVE_STREAM, PENDING_VERIFICATION_SYS_ACTIVE)
    },
    isCabinetDevice(device)
      ? {
        name: 'reboot_command',
        run: async () => publishDeviceCommand(device, ACTIONS_STREAM, REBOOT_COMMAND)
      }
      : {
        name: 'reload_command',
        run: async () => publishDeviceCommand(device, COMMAND_STREAM, RELOAD_COMMAND)
      }
  ];
}

function getDeviceSteps(device, direction: RetrofitDirection, date_in_seconds?: number) {
  return direction === 'mark' ? getMarkAsRetrofitSteps(device, date_in_seconds) : getReturnFromRetrofitSteps(device);
}

// Runs the full per-device sequence. Any failing step aborts that device's
// sequence; every step is idempotent so a retry can safely re-run from step 1.
// `date_in_seconds` backdates entry into Retrofit and is ignored when returning.
export async function runDeviceRetrofitSequence(device, direction: RetrofitDirection, date_in_seconds?: number): Promise<DeviceBatchResult> {
  const steps = getDeviceSteps(device, direction, date_in_seconds);

  for (const step of steps) {
    try {
      await step.run();
    } catch (e) {
      return {
        device_id: device.id,
        device_name: device.name,
        status: 'failed',
        failed_step: step.name,
        reason: getErrorReason(e)
      };
    }
  }

  return { device_id: device.id, device_name: device.name, status: 'success' };
}

// `e` is usually the raw fetch Response thrown by API.ts on a non-ok status
// (see `knock`/`knockStream`), so it carries an HTTP status code but no
// parsed body the caller can rely on. Status codes mean nothing to the user,
// so every branch here is translated into a plain-language explanation.
function getFriendlyErrorMessage(status?: number): string {
  if (!status) return i18n.t('Could not reach the server. Check your connection and try again') as string;
  if (status === 401) return i18n.t('Your session has expired, please log in again') as string;
  if (status === 403) return i18n.t("You're not authorized to perform this action") as string;
  if (status === 404) return i18n.t('The device could not be found') as string;
  if (status === 408 || status === 504) return i18n.t('The request took too long, please try again') as string;
  if (status === 409) return i18n.t('The device state changed, please try again') as string;
  if (status === 429) return i18n.t('Too many requests, please wait a moment and try again') as string;
  if (status >= 500) return i18n.t('The server encountered a problem, please try again later') as string;
  if (status >= 400) return i18n.t('The request could not be completed, please try again') as string;
  return i18n.t('Unknown error') as string;
}

function getErrorReason(e) {
  if (!e) return i18n.t('Unknown error') as string;
  if (e.status) return getFriendlyErrorMessage(e.status);
  // Errors thrown by this module itself (e.g. "device.schedule was not
  // reset") are already user-facing, plain-language sentences - translate
  // them as-is instead of the generic fallback.
  if (e.message) return i18n.t(e.message) as string;
  return getFriendlyErrorMessage();
}

export function markDeviceAsRetrofit(device, entry_date?: string): Promise<DeviceBatchResult> {
  return runDeviceRetrofitSequence(device, 'mark', resolveRetrofitEntryDate(entry_date));
}

export function returnDeviceFromRetrofit(device): Promise<DeviceBatchResult> {
  return runDeviceRetrofitSequence(device, 'return');
}

// `entry_date` is a 'YYYY-MM-DD' day picked by the user. It is resolved once here rather
// than per device, so every device in the batch carries the same entry timestamp instead
// of drifting by however long the pool takes to reach it.
export function runRetrofitBatch(devices, direction: RetrofitDirection, on_progress?, entry_date?: string): Promise<DeviceBatchResult[]> {
  const date_in_seconds = direction === 'mark' ? resolveRetrofitEntryDate(entry_date) : undefined;

  return runAdaptivePool<any, DeviceBatchResult>(devices, (device) => runDeviceRetrofitSequence(device, direction, date_in_seconds), {
    concurrency: 5,
    min_concurrency: 1,
    is_failure: (result) => !result || result.status === 'failed',
    on_progress
  });
}

export function runMarkAsRetrofitBatch(devices, on_progress?, entry_date?: string): Promise<DeviceBatchResult[]> {
  return runRetrofitBatch(devices, 'mark', on_progress, entry_date);
}

export function runReturnFromRetrofitBatch(devices, on_progress?): Promise<DeviceBatchResult[]> {
  return runRetrofitBatch(devices, 'return', on_progress);
}
